import React, { useCallback, useEffect, useState } from "react";
import { Button, CustomSelect, Error, Label } from "../..";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { generateKey } from "../../../../utils/generateKey";
import { FormModalSchema } from "../../../Schema/Configuration/FormSchema";
import { shallowEqual, useSelector } from "react-redux";
import { getAxios, postAxios, putAxios } from "../../../Services/commonService";
import {
  API_CREATE_FORM,
  API_GET_FORM_BY_ID,
  API_GET_PERMISSION,
  API_UPDATE_FORM,
} from "../../../../utils/api/ApiConstant";
import TreeSelect from "../../UI/TreeSelect";
import { generatePath } from "../../../../utils/pathGenerator";

const FormModal = ({ handleModalHide, rowData, fetchData }) => {
  const isEdit = rowData?.id && Object.keys(rowData)?.length > 0;

  const [permissionList, setPermissionList] = useState([]);
  const [formData, setFormData] = useState(null);

  //  const { parentIdList } = useSelector(
  //   (state) => ({
  //     parentIdList: state?.user?.parentIdList,
  //   }),
  //   shallowEqual,
  // );
  const findParentLabel = useCallback((parentId, tree) => {
    for (const node of tree) {
      if (node.value === parentId) {
        return node.label;
      }
      if (node.children && node.children.length > 0) {
        const found = findParentLabel(parentId, node.children);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: null,
      key: null,
      parentId: null,
      path: null,
      displayIndex: null,
      description: null,
      permissions: null,
    },
    resolver: yupResolver(FormModalSchema),
  });

  const watchName = watch("name");
  const watchParentId = watch("parentId");

  // ✅ Auto-generate path when name or parentId changes
  useEffect(() => {
    const generatedPath = generatePath(
      watchParentId,
      rowData?.parentIdList,
      watchName,
    );
    setValue("path", generatedPath);
  }, [watchName, watchParentId, rowData?.parentIdList, setValue, isEdit]);

  const fetchPermissionData = useCallback(async () => {
    const response = await getAxios(API_GET_PERMISSION);
    const list = response.map((permission) => ({
      label: permission?.name,
      value: permission?.id,
    }));
    setPermissionList(list);
  }, []);

  const fetchFormDetail = useCallback(async () => {
    if (!isEdit) return;
    try {
      const response = await getAxios(`${API_GET_FORM_BY_ID}/${rowData.id}`);
      console.log("response", response);
      setFormData(response);
    } catch (error) {
      console.error("Failed to fetch form detail:", error);
    }
  }, [isEdit, rowData?.id]);

  useEffect(() => {
    fetchFormDetail();
    fetchPermissionData();
  }, [fetchPermissionData, fetchFormDetail]);

  useEffect(() => {
    if (isEdit && formData && permissionList.length > 0) {
      const assignedPermissionIds =
        formData.permissions?.map((perm) => perm.permissionId) ?? [];
      const mappedPermissions = permissionList
        .filter((perm) => assignedPermissionIds.includes(perm.value))
        .map((perm) => {
          const original = formData.permissions.find(
            (p) => p.permissionId === perm.value,
          );
          return {
            label: perm.label,
            value: perm.value,
            id: original?.id,
            formId: original?.formId,
            isActive: original?.isActive,
            rowVersion: original?.rowVersion,
          };
        });

      reset({
        name: formData.name ?? null,
        key: formData.key ?? null,
        parentId: formData.parentId
          ? {
              label:
                findParentLabel(
                  formData.parentId,
                  rowData?.parentIdList || [],
                ) ?? String(formData.parentId),
              value: formData.parentId,
            }
          : null,
        path: formData.path ?? null,
        displayIndex: formData.displayIndex ?? null,
        description: formData.description ?? null,
        permissions: mappedPermissions,
      });
    }
  }, [
    isEdit,
    formData,
    permissionList,
    reset,
    findParentLabel,
    rowData?.parentIdList,
  ]);
  console.log("formData", formData);
  const handleFormSubmit = async (payload) => {
    const transformedPayload = {
      ...payload,
      parentId: payload?.parentId ? payload?.parentId?.value : null,
      permissions: payload?.permissions
        ? payload.permissions.map((item) => item.value)
        : null,
    };
    console.log("payload", payload);
    // console.log("transformedPayload", transformedPayload);
    let res;
    if (isEdit) {
      const updatePayload = {
        name: transformedPayload?.name,
        key: transformedPayload?.key,
        parentId: transformedPayload?.parentId,
        path: transformedPayload?.path,
        displayIndex: transformedPayload?.displayIndex,
        description: transformedPayload?.description,
        permissions: transformedPayload?.permissions,
        isActive: formData?.isActive,
        rowVersion: formData?.rowVersion,
      };
      res = await putAxios(`${API_UPDATE_FORM}/${rowData?.id}`, updatePayload);
    } else {
      res = await postAxios(API_CREATE_FORM, transformedPayload);
    }

    if (res) {
      fetchData();
      handleModalHide();
    }
  };
  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="row m-0 p-0">
        <div className="col-lg-6 col-12 mb-2">
          <Label htmlFor="name" className="form-label" isRequired={true}>
            Name
          </Label>
          <input
            className="form-control"
            type="text"
            id="name"
            placeholder="enter name"
            {...register("name")}
          />
          {errors.name && <Error>{errors.name.message}</Error>}
        </div>
        <div className="col-md-6 m-0 p-1">
          <Label htmlFor="key" className="form-label" isRequired={true}>
            Key
          </Label>
          <input
            className="form-control"
            type="text"
            id="key"
            placeholder="enter key"
            {...register("key")}
          />
          {errors.key && <Error>{errors.key.message}</Error>}
        </div>
        <div className="col-xl-2 col-lg-3 col-12">
          <div className="input_control">
            <Label htmlFor="parentId" className="form-label">
              ParentId
            </Label>
            <Controller
              name="parentId"
              control={control}
              render={({ field }) => (
                <TreeSelect
                  options={rowData?.parentIdList}
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                  isClearable={true}
                  placeholder="Select parent..."
                />
              )}
            />
            {errors.parentId && <Error>{errors.parentId.message}</Error>}
          </div>
        </div>
        <div className="col-md-6 m-0 p-1">
          <Label htmlFor="path" className="form-label" isRequired={true}>
            Path
          </Label>
          <input
            className="form-control"
            type="text"
            id="path"
            placeholder="enter path"
            {...register("path")}
          />
          {errors.path && <Error>{errors.path.message}</Error>}
        </div>
        <div className="col-md-6 m-0 p-1">
          <Label
            htmlFor="displayIndex"
            className="form-label"
            isRequired={true}
          >
            DisplayIndex
          </Label>
          <input
            className="form-control"
            type="text"
            id="displayIndex"
            placeholder="enter displayIndex"
            {...register("displayIndex")}
          />
          {errors.displayIndex && <Error>{errors.displayIndex.message}</Error>}
        </div>
        <div className="col-md-6 m-0 p-1">
          <Label htmlFor="description" className="form-label">
            Description
          </Label>
          <input
            className="form-control"
            type="text"
            id="description"
            placeholder="enter description"
            {...register("description")}
          />
          {errors.description && <Error>{errors.description.message}</Error>}
        </div>
        <div className="col-12">
          <div className="input_control">
            <Label className="form-label">Permissions</Label>
            <Controller
              name="permissions"
              control={control}
              render={({ field }) => (
                <div className="d-flex flex-wrap gap-3">
                  {permissionList.map((permission) => {
                    const isChecked = field.value
                      ? field.value.some(
                          (item) => item.value === permission.value,
                        )
                      : false;

                    const handleCheckboxChange = (e) => {
                      if (e.target.checked) {
                        field.onChange([
                          ...(field.value || []),
                          { label: permission.label, value: permission.value },
                        ]);
                      } else {
                        field.onChange(
                          field.value.filter(
                            (item) => item.value !== permission.value,
                          ),
                        );
                      }
                    };

                    return (
                      <div className="form-check" key={permission.value}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`permission-${permission.value}`}
                          checked={isChecked}
                          onChange={handleCheckboxChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`permission-${permission.value}`}
                        >
                          {permission.label}
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            />
            {errors.permissions && <Error>{errors.permissions.message}</Error>}
          </div>
        </div>
      </div>
      <Button type="submit" className="btn btn_main">
        {isEdit ? "Update" : "Create"}
      </Button>
      <Button type="button" className="btn btn_main" onClick={handleModalHide}>
        Close
      </Button>
    </form>
  );
};

export default FormModal;
